import { useState, useEffect, useCallback } from 'react';
import {
  Calendar, Zap, Bell, Play, Trash2, Loader2, Plus, X,
  CheckCircle, AlertTriangle, Clock, Power
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { authFetch } from '../lib/api';

const API = process.env.REACT_APP_BACKEND_URL;

const CADENCE_LABEL = { daily: 'Daily', weekly: 'Weekly (Mon)', monthly: 'Monthly (1st)' };

// ═══════════════ SCHEDULES SECTION ═══════════════
const SchedulesSection = ({ account, agents, schedules, onRefresh }) => {
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ agent_type: '', cadence: 'daily', hour_utc: 6, date_range: 'LAST_30_DAYS' });
  const [busy, setBusy] = useState(null);

  const eligibleAgents = agents.filter(a => (a.phase === 1 || a.phase === 2) && a.type !== 'synthesis');
  const isPmaxAudit = form.agent_type === 'pmax_audit';

  const create = async () => {
    if (!form.agent_type) { toast.error('Select an agent'); return; }
    setBusy('create');
    try {
      const res = await authFetch(`${API}/api/optimization/accounts/${account.id}/schedules`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to create schedule');
      }
      toast.success('Schedule created');
      setShowAdd(false);
      setForm({ agent_type: '', cadence: 'daily', hour_utc: 6, date_range: 'LAST_30_DAYS' });
      onRefresh();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(null); }
  };

  const toggle = async (s) => {
    setBusy(s.id);
    try {
      await authFetch(`${API}/api/optimization/schedules/${s.id}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: !s.enabled }),
      });
      onRefresh();
    } catch { toast.error('Toggle failed'); }
    finally { setBusy(null); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete schedule: ${s.agent_name} (${s.cadence})?`)) return;
    setBusy(s.id);
    try {
      await authFetch(`${API}/api/optimization/schedules/${s.id}`, { method: 'DELETE' });
      toast.success('Schedule deleted');
      onRefresh();
    } catch { toast.error('Delete failed'); }
    finally { setBusy(null); }
  };

  const runNow = async (s) => {
    setBusy(s.id);
    try {
      const res = await authFetch(`${API}/api/optimization/schedules/${s.id}/run-now`, { method: 'POST' });
      if (!res.ok) throw new Error('Run-now failed');
      toast.success(`${s.agent_name} fired — check Recent Runs in ~30s`);
    } catch (e) { toast.error(e.message); }
    finally { setBusy(null); }
  };

  return (
    <div className="space-y-3" data-testid="schedules-section">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Calendar size={16} className="text-brand" />
          <h3 className="text-sm font-semibold text-text-primary">Scheduled Runs ({schedules.length})</h3>
        </div>
        <Button size="sm" variant="outline" onClick={() => setShowAdd(!showAdd)} data-testid="add-schedule-btn">
          <Plus size={14} className="mr-1" /> New Schedule
        </Button>
      </div>

      {showAdd && (
        <Card className="bg-surface-page" data-testid="add-schedule-form">
          <CardContent className="p-4 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Agent</Label>
                <select
                  className="w-full mt-1 border border-border-subtle rounded px-2 py-1.5 text-sm bg-white"
                  value={form.agent_type}
                  onChange={e => setForm({ ...form, agent_type: e.target.value })}
                  data-testid="schedule-agent-select"
                >
                  <option value="">Select agent...</option>
                  {eligibleAgents.map(a => <option key={a.type} value={a.type}>{a.name}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs">Cadence</Label>
                <select
                  className="w-full mt-1 border border-border-subtle rounded px-2 py-1.5 text-sm bg-white"
                  value={form.cadence}
                  onChange={e => setForm({ ...form, cadence: e.target.value })}
                  data-testid="schedule-cadence-select"
                >
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly (Mon)</option>
                  <option value="monthly">Monthly (1st)</option>
                </select>
              </div>
              <div>
                <Label className="text-xs">Hour (UTC)</Label>
                <Input
                  type="number" min={0} max={23}
                  value={form.hour_utc}
                  onChange={e => setForm({ ...form, hour_utc: parseInt(e.target.value) || 0 })}
                  data-testid="schedule-hour-input"
                />
              </div>
            </div>
            {isPmaxAudit && (
              <div data-testid="schedule-pmax-extra">
                <Label className="text-xs">Date Range (PMax Audit)</Label>
                <select
                  className="w-full mt-1 border border-border-subtle rounded px-2 py-1.5 text-sm bg-white"
                  value={form.date_range}
                  onChange={e => setForm({ ...form, date_range: e.target.value })}
                  data-testid="schedule-date-range-select"
                >
                  <option value="LAST_7_DAYS">Last 7 days</option>
                  <option value="LAST_14_DAYS">Last 14 days</option>
                  <option value="LAST_30_DAYS">Last 30 days</option>
                  <option value="LAST_90_DAYS">Last 90 days</option>
                  <option value="THIS_MONTH">This month</option>
                  <option value="LAST_MONTH">Last month</option>
                </select>
                <p className="text-[11px] text-text-tertiary mt-1">
                  Each scheduled run creates a new audit document in the Paid Search Audits page.
                </p>
              </div>
            )}
            <div className="flex gap-2 justify-end">
              <Button size="sm" variant="ghost" onClick={() => setShowAdd(false)}>Cancel</Button>
              <Button size="sm" onClick={create} disabled={busy === 'create'} data-testid="save-schedule-btn" className="bg-brand hover:bg-brand-hover">
                {busy === 'create' ? <Loader2 size={14} className="mr-1 animate-spin" /> : null}
                Create
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {schedules.length === 0 ? (
        <Card className="py-8 text-center" data-testid="no-schedules">
          <Clock size={28} className="mx-auto text-text-muted mb-2" />
          <p className="text-sm text-text-tertiary">No schedules yet. Add one to automate agent runs.</p>
        </Card>
      ) : (
        <div className="space-y-2">
          {schedules.map(s => (
            <Card key={s.id} data-testid={`schedule-${s.id}`}>
              <CardContent className="p-3">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => toggle(s)}
                    disabled={busy === s.id}
                    className={`p-1.5 rounded-full ${s.enabled ? 'bg-emerald-100 text-emerald-600' : 'bg-gray-100 text-gray-400'}`}
                    title={s.enabled ? 'Enabled' : 'Disabled'}
                    data-testid={`toggle-schedule-${s.id}`}
                  >
                    <Power size={12} />
                  </button>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-text-primary">{s.agent_name}</p>
                    <div className="flex items-center gap-2 text-xs text-text-tertiary mt-0.5">
                      <span>{CADENCE_LABEL[s.cadence] || s.cadence}</span>
                      <span>•</span>
                      <span>{String(s.hour_utc).padStart(2, '0')}:00 UTC</span>
                      {s.last_run_at && (
                        <>
                          <span>•</span>
                          <span>Last: {new Date(s.last_run_at).toLocaleString()}</span>
                        </>
                      )}
                      {s.is_default && <span className="px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded text-[10px]">default</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" onClick={() => runNow(s)} disabled={busy === s.id} title="Run now" data-testid={`run-now-${s.id}`}>
                      <Play size={14} />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => remove(s)} disabled={busy === s.id} data-testid={`delete-schedule-${s.id}`}>
                      <Trash2 size={14} className="text-red-500" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

// ═══════════════ TRIGGERS SECTION ═══════════════
const TriggersSection = ({ account, triggers, metrics, onRefresh }) => {
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ metric: '', threshold: '' });
  const [busy, setBusy] = useState(null);

  const selectedMeta = metrics.find(m => m.metric === form.metric);

  const create = async () => {
    if (!form.metric) { toast.error('Select a metric'); return; }
    setBusy('create');
    try {
      const body = {
        metric: form.metric,
        threshold: form.threshold ? parseFloat(form.threshold) : undefined,
      };
      const res = await authFetch(`${API}/api/optimization/accounts/${account.id}/triggers`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to create trigger');
      }
      toast.success('Trigger created');
      setShowAdd(false);
      setForm({ metric: '', threshold: '' });
      onRefresh();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(null); }
  };

  const toggle = async (t) => {
    setBusy(t.id);
    try {
      await authFetch(`${API}/api/optimization/triggers/${t.id}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: !t.enabled }),
      });
      onRefresh();
    } catch { toast.error('Toggle failed'); }
    finally { setBusy(null); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete trigger: ${t.metric_label}?`)) return;
    setBusy(t.id);
    try {
      await authFetch(`${API}/api/optimization/triggers/${t.id}`, { method: 'DELETE' });
      toast.success('Trigger deleted');
      onRefresh();
    } catch { toast.error('Delete failed'); }
    finally { setBusy(null); }
  };

  return (
    <div className="space-y-3" data-testid="triggers-section">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Zap size={16} className="text-brand" />
          <h3 className="text-sm font-semibold text-text-primary">Event Triggers ({triggers.length})</h3>
        </div>
        <Button size="sm" variant="outline" onClick={() => setShowAdd(!showAdd)} data-testid="add-trigger-btn">
          <Plus size={14} className="mr-1" /> New Trigger
        </Button>
      </div>

      {showAdd && (
        <Card className="bg-surface-page" data-testid="add-trigger-form">
          <CardContent className="p-4 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Metric</Label>
                <select
                  className="w-full mt-1 border border-border-subtle rounded px-2 py-1.5 text-sm bg-white"
                  value={form.metric}
                  onChange={e => setForm({ ...form, metric: e.target.value, threshold: '' })}
                  data-testid="trigger-metric-select"
                >
                  <option value="">Select metric...</option>
                  {metrics.map(m => <option key={m.metric} value={m.metric}>{m.label}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs">
                  Threshold {selectedMeta ? `(${selectedMeta.unit})` : ''}
                </Label>
                <Input
                  type="number" step="0.1"
                  placeholder={selectedMeta ? `Default: ${selectedMeta.default_threshold}` : 'e.g. 25'}
                  value={form.threshold}
                  onChange={e => setForm({ ...form, threshold: e.target.value })}
                  data-testid="trigger-threshold-input"
                />
              </div>
            </div>
            {selectedMeta && (
              <p className="text-xs text-text-tertiary">
                <b>Fires when:</b> {selectedMeta.description}. Will trigger <b>{selectedMeta.agent}</b> agent.
              </p>
            )}
            <div className="flex gap-2 justify-end">
              <Button size="sm" variant="ghost" onClick={() => setShowAdd(false)}>Cancel</Button>
              <Button size="sm" onClick={create} disabled={busy === 'create'} data-testid="save-trigger-btn" className="bg-brand hover:bg-brand-hover">
                {busy === 'create' ? <Loader2 size={14} className="mr-1 animate-spin" /> : null}
                Create
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {triggers.length === 0 ? (
        <Card className="py-8 text-center" data-testid="no-triggers">
          <Zap size={28} className="mx-auto text-text-muted mb-2" />
          <p className="text-sm text-text-tertiary">No triggers yet. Add a rule to auto-detect issues.</p>
        </Card>
      ) : (
        <div className="space-y-2">
          {triggers.map(t => {
            const meta = metrics.find(m => m.metric === t.metric);
            return (
              <Card key={t.id} data-testid={`trigger-${t.id}`}>
                <CardContent className="p-3">
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => toggle(t)}
                      disabled={busy === t.id}
                      className={`p-1.5 rounded-full ${t.enabled ? 'bg-emerald-100 text-emerald-600' : 'bg-gray-100 text-gray-400'}`}
                      data-testid={`toggle-trigger-${t.id}`}
                    >
                      <Power size={12} />
                    </button>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-text-primary">{t.metric_label}</p>
                      <div className="flex items-center gap-2 text-xs text-text-tertiary mt-0.5">
                        <span>Threshold: {t.threshold} {meta?.unit || ''}</span>
                        <span>•</span>
                        <span>Fires: {t.fire_count || 0}×</span>
                        {t.last_fired_at && (
                          <>
                            <span>•</span>
                            <span>Last: {new Date(t.last_fired_at).toLocaleString()}</span>
                          </>
                        )}
                        {t.is_default && <span className="px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded text-[10px]">default</span>}
                      </div>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => remove(t)} disabled={busy === t.id} data-testid={`delete-trigger-${t.id}`}>
                      <Trash2 size={14} className="text-red-500" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ═══════════════ ALERTS SECTION ═══════════════
const AlertsSection = ({ account, alerts, onRefresh }) => {
  const [busy, setBusy] = useState(null);

  const runAgent = async (a) => {
    setBusy(a.id);
    try {
      const res = await authFetch(`${API}/api/optimization/alerts/${a.id}/run`, { method: 'POST' });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Run failed');
      }
      toast.success('Agent run complete');
      onRefresh();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(null); }
  };

  const dismiss = async (a) => {
    setBusy(a.id);
    try {
      await authFetch(`${API}/api/optimization/alerts/${a.id}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'dismissed' }),
      });
      toast.success('Alert dismissed');
      onRefresh();
    } catch { toast.error('Dismiss failed'); }
    finally { setBusy(null); }
  };

  const pending = alerts.filter(a => a.status === 'pending');
  const history = alerts.filter(a => a.status !== 'pending').slice(0, 10);

  return (
    <div className="space-y-3" data-testid="alerts-section">
      <div className="flex items-center gap-2">
        <Bell size={16} className="text-brand" />
        <h3 className="text-sm font-semibold text-text-primary">Alerts ({pending.length} pending)</h3>
      </div>

      {pending.length === 0 ? (
        <Card className="py-8 text-center" data-testid="no-pending-alerts">
          <CheckCircle size={28} className="mx-auto text-emerald-500 mb-2" />
          <p className="text-sm text-text-tertiary">No pending alerts. Everything's quiet.</p>
        </Card>
      ) : (
        <div className="space-y-2">
          {pending.map(a => (
            <Card key={a.id} className="bg-amber-50 border-amber-200" data-testid={`alert-${a.id}`}>
              <CardContent className="p-3">
                <div className="flex items-start gap-3">
                  <AlertTriangle size={18} className="text-amber-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-amber-900">{a.metric_label}</p>
                    <p className="text-xs text-amber-800 mt-0.5">{a.evidence}</p>
                    <p className="text-[11px] text-amber-700 mt-1">
                      Triggered {new Date(a.created_at).toLocaleString()} • Agent: {a.agent_type}
                    </p>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <Button size="sm" onClick={() => runAgent(a)} disabled={busy === a.id} data-testid={`run-alert-${a.id}`} className="bg-brand hover:bg-brand-hover">
                      {busy === a.id ? <Loader2 size={12} className="mr-1 animate-spin" /> : <Play size={12} className="mr-1" />}
                      Run Agent
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => dismiss(a)} disabled={busy === a.id} data-testid={`dismiss-alert-${a.id}`}>
                      <X size={14} />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {history.length > 0 && (
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-text-muted mt-4 mb-2">Recent History</p>
          <div className="space-y-1">
            {history.map(a => (
              <div key={a.id} className="flex items-center gap-2 text-xs text-text-tertiary py-1.5 px-2" data-testid={`alert-history-${a.id}`}>
                {a.status === 'ran' ? <CheckCircle size={12} className="text-emerald-500" /> : <X size={12} className="text-text-muted" />}
                <span className="flex-1 truncate">{a.metric_label} — {a.evidence}</span>
                <span>{new Date(a.created_at).toLocaleDateString()}</span>
                <span className="text-[10px] uppercase">{a.status}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ═══════════════ MAIN AUTOMATION PANEL ═══════════════
const OptimizationAutomation = ({ account, agents }) => {
  const [schedules, setSchedules] = useState([]);
  const [triggers, setTriggers] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [metrics, setMetrics] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!account) return;
    try {
      const [sRes, tRes, aRes, mRes] = await Promise.all([
        authFetch(`${API}/api/optimization/accounts/${account.id}/schedules`),
        authFetch(`${API}/api/optimization/accounts/${account.id}/triggers`),
        authFetch(`${API}/api/optimization/accounts/${account.id}/alerts`),
        authFetch(`${API}/api/optimization/trigger-metrics`),
      ]);
      if (sRes.ok) setSchedules((await sRes.json()).schedules || []);
      if (tRes.ok) setTriggers((await tRes.json()).triggers || []);
      if (aRes.ok) setAlerts((await aRes.json()).alerts || []);
      if (mRes.ok) setMetrics((await mRes.json()).metrics || []);
    } catch (e) { console.error('Automation load error:', e); }
    finally { setLoading(false); }
  }, [account]);

  useEffect(() => { load(); }, [load]);

  if (!account) return null;
  if (loading) return <div className="flex justify-center py-10"><Loader2 className="animate-spin text-brand" size={24} /></div>;

  return (
    <div className="space-y-8" data-testid="automation-panel">
      <AlertsSection account={account} alerts={alerts} onRefresh={load} />
      <SchedulesSection account={account} agents={agents} schedules={schedules} onRefresh={load} />
      <TriggersSection account={account} triggers={triggers} metrics={metrics} onRefresh={load} />
    </div>
  );
};

export default OptimizationAutomation;
